/**
 * FilterBuilder — Painel de busca avançada (query builder visual).
 * Inspirado em Jira/Delphi: campos principais visíveis + ocultos via "+ Filtro".
 * Suporta undo/redo, operadores por tipo, e lookup recursivo.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { FaPlus, FaSearch, FaUndo, FaRedo, FaTimes, FaChevronDown, FaChevronUp, FaFilter } from 'react-icons/fa';
import { FilterSchema, AppliedFilter, Operator, operatorLabels, BOOLEAN_UNSET } from './types';
import { useFilterHistory } from './useFilterHistory';
import { describeFilter } from './serialize';
import FilterField from './FilterField';

interface Props {
    schema: FilterSchema[];
    onSearch: (filters: AppliedFilter[]) => void;
    loading?: boolean;
    className?: string;
    /** Permite recolher o painel para liberar espaço depois de filtrar (default: true). */
    collapsible?: boolean;
    /** Começa recolhido. */
    defaultCollapsed?: boolean;
    /** Rótulo do cabeçalho quando colapsável. */
    title?: string;
    /**
     * Filtros já aplicados ao montar — para deep-link (outra tela navega para cá com filtro).
     * NÃO dispara onSearch: quem navega já semeia o próprio estado, senão haveria busca dobrada.
     * Precisa ser memoizado no chamador (array novo a cada render re-semearia).
     */
    initialFilters?: Omit<AppliedFilter, 'id'>[];
}

let filterId = 0;
const nextId = () => `f_${++filterId}`;

/**
 * O filtro está VALENDO (entra na busca e no resumo)?
 *
 * Booleano vale sempre: o operador É o valor ("sim"/"não"), e o `value` dele é `null` por
 * construção. Testar `value != null` — que é o certo para os outros tipos — o descartaria
 * silenciosamente, e era por isso que "Em cobrança: sim" sumia do resumo enquanto a busca ia
 * com `filter[collect]=true` na URL: duas regras diferentes para a mesma pergunta.
 */
const isApplied = (f: AppliedFilter): boolean => {
    // ⚠️ Booleano NÃO é "sempre aplicado". Ele era, e por isso uma linha reposta pelo `panelRows`
    // voltava valendo o operador padrão — o usuário tirava "Em cobrança", pesquisava, e ele
    // reaparecia como Sim filtrando de novo. Quem responde por um booleano é o operador
    if (f.type === 'boolean') return f.operator === 'is_true' || f.operator === 'is_false';
    if (Array.isArray(f.value)) return f.value.some(v => v !== '');
    return f.value !== '' && f.value !== null && f.value !== undefined;
};

/**
 * A linha VAZIA de um campo — a que o painel mostra sem ninguém ter pedido nada.
 *
 * ⚠️ No booleano ela nasce em `BOOLEAN_UNSET`, e não no `defaultOperator` do schema: o default
 * descreve o que o campo assume quando alguém o ESCOLHE em "+ Filtro", não o que ele vale por
 * existir na tela. Nascer no default fazia todo campo booleano visível filtrar sozinho.
 */
const emptyRow = (s: FilterSchema): AppliedFilter => ({
    id: nextId(), key: s.key, label: s.label, type: s.type,
    operator: s.type === 'boolean' ? BOOLEAN_UNSET : s.defaultOperator,
    value: s.type === 'boolean' ? null : '',
});

/**
 * As linhas do painel para um conjunto aplicado: o que está valendo, mais os campos `visible` do
 * schema que ficaram sem valor.
 *
 * Sem esse preenchimento, aplicar um filtro fazia os demais campos visíveis DESAPARECEREM do
 * painel — quem chegava na tela com filtro semeado (deep-link, ou o padrão do item de menu) só via
 * o filtro semeado e tinha de reencontrar os outros em "+ Filtro". A ordem é a do schema, para o
 * painel não se reorganizar sozinho a cada busca.
 */
const panelRows = (applied: AppliedFilter[], schema: FilterSchema[]): AppliedFilter[] => {
    const byKey = new Map(applied.map(f => [f.key, f]));
    const visibleRows = schema.filter(s => s.visible).map(s => byKey.get(s.key) ?? emptyRow(s));
    // aplicado que não é campo visível do schema (deep-link em campo oculto) vira linha extra,
    // senão o filtro valeria sem aparecer
    const extras = applied.filter(f => !schema.some(s => s.visible && s.key === f.key));
    return [...visibleRows, ...extras];
};

const FilterBuilder: React.FC<Props> = ({
    schema, onSearch, loading, className,
    collapsible = true, defaultCollapsed = false, title = 'Filtros',
    initialFilters,
}) => {
    const { current, push, undo, redo, reset, canUndo, canRedo } = useFilterHistory();
    const [filters, setFilters] = useState<AppliedFilter[]>([]);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);

    // Sync with history (undo/redo e a semeadura inicial).
    // `panelRows` é o que impede o painel de encolher: `current` guarda só o que está VALENDO, e
    // jogá-lo cru no painel apagava os campos visíveis que ficaram vazios.
    useEffect(() => { setFilters(prev => (current.length ? panelRows(current, schema) : prev)); },
        // schema é constante por tela; incluí-lo só re-rodaria o efeito à toa
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [current]);

    // Initialize with visible fields (no value = not applied yet), já aplicando o que veio de fora
    useEffect(() => {
        const fromOutside = (key: string) => initialFilters?.find(f => f.key === key);
        const visibleOnes: AppliedFilter[] = schema
            .filter(s => s.visible)
            .map(s => {
                const deepLinked = fromOutside(s.key);
                if (!deepLinked) return emptyRow(s);
                return {
                    id: nextId(),
                    key: s.key,
                    label: s.label,
                    type: s.type,
                    operator: deepLinked.operator,
                    value: deepLinked.value,
                };
            });
        // deep-link em campo oculto entra como chip extra, senão o filtro valeria sem aparecer
        const extras: AppliedFilter[] = (initialFilters || [])
            .filter(f => !visibleOnes.some(v => v.key === f.key))
            .map(f => ({ ...f, id: nextId() }));
        const allFilters = [...visibleOnes, ...extras];
        setFilters(allFilters);
        // registra no histórico para o resumo do painel colapsado mostrar o que está valendo.
        // Só o que veio DE FORA: um campo visível vazio não é filtro, e o booleano visível só
        // conta quando alguém o declarou — senão a tela abriria dizendo que filtra por ele sem
        // ninguém ter pedido.
        const semeados = allFilters.filter(f => fromOutside(f.key) && isApplied(f));
        if (semeados.length) push(semeados);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema, initialFilters]);

    const addFilter = (field: FilterSchema) => {
        const newFilter: AppliedFilter = {
            id: nextId(),
            key: field.key,
            label: field.label,
            type: field.type,
            operator: field.defaultOperator,
            value: field.type === 'boolean' ? null : '',
        };
        setFilters(prev => [...prev, newFilter]);
        setShowAddMenu(false);
    };

    const updateFilter = (id: string, updated: AppliedFilter) => {
        setFilters(prev => prev.map(f => f.id === id ? updated : f));
    };

    const removeFilter = (id: string) => {
        setFilters(prev => prev.filter(f => f.id !== id));
    };

    const handleSearch = useCallback(() => {
        const active = filters.filter(isApplied);
        push(active);
        onSearch(active);
    }, [filters, push, onSearch]);

    const handleReset = () => {
        reset();
        setFilters(schema.filter(s => s.visible).map(emptyRow));
        onSearch([]);
    };

    const handleUndo = () => { undo(); };
    const handleRedo = () => { redo(); };

    // Keyboard shortcuts
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
            if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); handleRedo(); }
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleSearch(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [handleSearch]);

    // Campos disponíveis para adicionar: qualquer um do schema que não esteja na tela.
    // (inclui os `visible` que o usuário removeu — antes eles nunca voltavam ao menu)
    const availableFields = schema.filter(s => !filters.find(f => f.key === s.key));

    // Resumo do que está aplicado (última busca), mostrado quando o painel está colapsado.
    const appliedSummary = useMemo(
        () => current.map(f => describeFilter(f, operatorLabels[f.operator])),
        [current]
    );

    return (
        <div className={`bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg p-3 mb-1 relative overflow-visible ${className || ''}`}>
            {/* Cabeçalho (colapsar) */}
            {collapsible && (
                <button onClick={() => setCollapsed(v => !v)}
                    className={`w-full flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white ${collapsed ? '' : 'mb-2 pb-2 border-b dark:border-slate-700'}`}>
                    <FaFilter size={10} className="text-gray-400 shrink-0" />
                    <span className="shrink-0">{title}</span>
                    {appliedSummary.length > 0 && (
                        <span className="px-1.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 text-[10px] shrink-0">
                            {appliedSummary.length} aplicado{appliedSummary.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {collapsed && appliedSummary.length > 0 && (
                        <span className="truncate font-normal text-gray-400 dark:text-gray-500" title={appliedSummary.join(' · ')}>
                            {appliedSummary.join(' · ')}
                        </span>
                    )}
                    <span className="flex-1" />
                    {collapsed ? <FaChevronDown size={11} className="shrink-0" /> : <FaChevronUp size={11} className="shrink-0" />}
                </button>
            )}

            {!collapsed && (
                <>
                    {/* Active filters */}
                    <div className="space-y-1.5">
                        {filters.map(filter => {
                            const fieldSchema = schema.find(s => s.key === filter.key);
                            if (!fieldSchema) return null;
                            return (
                                <FilterField
                                    key={filter.id}
                                    filter={filter}
                                    schema={fieldSchema}
                                    onChange={updated => updateFilter(filter.id, updated)}
                                    onRemove={() => removeFilter(filter.id)}
                                />
                            );
                        })}
                    </div>

                    {/* Actions bar */}
                    <div className="flex items-center gap-2 mt-3 pt-2 border-t dark:border-slate-700">
                        {/* Add filter */}
                        <div className="relative">
                            <button onClick={() => setShowAddMenu(!showAddMenu)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-slate-700 rounded">
                                <FaPlus size={9} /> Filtro
                            </button>
                            {showAddMenu && availableFields.length > 0 && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 border dark:border-slate-600 rounded shadow-lg z-50 py-1 min-w-[200px] max-h-[250px] overflow-y-auto">
                                    {availableFields.map(field => (
                                        <button key={field.key} onClick={() => addFilter(field)}
                                            className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">
                                            {field.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Undo / Redo */}
                        <button onClick={handleUndo} disabled={!canUndo} title="Desfazer (Ctrl+Z)"
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed">
                            <FaUndo size={11} />
                        </button>
                        <button onClick={handleRedo} disabled={!canRedo} title="Refazer (Ctrl+Y)"
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed">
                            <FaRedo size={11} />
                        </button>

                        <div className="flex-1" />

                        {/* Reset */}
                        <button onClick={handleReset}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-500">
                            <FaTimes size={9} /> Limpar
                        </button>

                        {/* Search */}
                        <button onClick={handleSearch} disabled={loading}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                            <FaSearch size={10} /> {loading ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default FilterBuilder;
